// Discount shortages — pure helper for detecting items sold below
// their standard price.
//
// A discount shortage = (product.unitPrice − actualUnitPrice) × qtySold
// where actualUnitPrice = totalSalesRands / totalQtySold for that
// product on that day. Aggregation is per product code (multiple
// sale rows for the same code are summed first).
//
// Skip rules:
//   - product not found in the product list (no canonical price)
//   - saleType === 'continuous_mirror' (no fixed price to compare)
//   - product.unitPrice == 0 / null (no fixed price)
//   - qtySold <= 0
//
// Edge cases:
//   - Free items (actualPrice = 0 but expected > 0) are returned in
//     a separate `freeItems` array tagged `type: 'free'`. They are NOT
//     included in `items` or `totalDiscount` because being given away
//     for free is a different operational event from being discounted.
//   - Threshold: discountPerUnit must exceed 0.01 to count, avoiding
//     floating-point false positives on rounded prices.

import { getProductPrice } from './productPricing'

const EPSILON = 0.01

/** Compute discount shortages for a list of sale records against a
 *  product list. Returns:
 *    {
 *      items:               [{ productCode, productName, category, qty,
 *                              expectedPrice, actualPrice, discountPerUnit,
 *                              totalDiscount, type: 'discount' }],
 *      freeItems:           [{ productCode, productName, category, qty,
 *                              expectedPrice, actualPrice, totalLoss,
 *                              type: 'free' }],
 *      totalDiscount:       sum of items[].totalDiscount,
 *      totalFreeLoss:       sum of freeItems[].totalLoss,
 *      affectedProducts:    items.length,
 *      affectedFreeProducts: freeItems.length,
 *    }
 *  Both arrays are sorted by impact descending.
 */
export function computeDiscountShortages(sales, products, dept = null) {
  const productByCode = {}
  for (const p of (products || [])) {
    if (p && p.code) productByCode[String(p.code).toLowerCase()] = p
  }

  // Aggregate per product code first — sale records may have multiple
  // lines for the same product (e.g. one row per upload session).
  const aggregated = {}
  for (const sale of (sales || [])) {
    if (!sale || !sale.productCode) continue
    const key = String(sale.productCode).toLowerCase()
    if (!aggregated[key]) {
      aggregated[key] = {
        productCode: sale.productCode,
        qtySold: 0,
        salesRands: 0,
      }
    }
    aggregated[key].qtySold += Number(sale.qtySold) || 0
    aggregated[key].salesRands += Number(sale.salesRands) || 0
  }

  const items = []
  const freeItems = []

  for (const key of Object.keys(aggregated)) {
    const agg = aggregated[key]
    const product = productByCode[key]
    if (!product) continue                                // unknown product
    if (product.saleType === 'continuous_mirror') continue // no fixed price
    const priced = getProductPrice(product, dept)
    const expectedPrice = Number(priced) || 0
    if (expectedPrice <= 0) continue                       // no fixed price at this branch
    if (agg.qtySold <= 0) continue

    const actualPrice = agg.salesRands / agg.qtySold

    // Free item edge case: collected R0 against an expected price.
    // Tracked separately so dashboards can distinguish "discount" from
    // "given away free".
    if (actualPrice <= EPSILON) {
      const totalLoss = Math.round(expectedPrice * agg.qtySold * 100) / 100
      freeItems.push({
        productCode: product.code,
        productName: product.name,
        category: product.category,
        qty: agg.qtySold,
        expectedPrice: Math.round(expectedPrice * 100) / 100,
        actualPrice: 0,
        totalLoss,
        type: 'free',
      })
      continue
    }

    const discountPerUnit = expectedPrice - actualPrice
    if (discountPerUnit > EPSILON) {
      const totalDiscount = Math.round(discountPerUnit * agg.qtySold * 100) / 100
      items.push({
        productCode: product.code,
        productName: product.name,
        category: product.category,
        qty: agg.qtySold,
        expectedPrice: Math.round(expectedPrice * 100) / 100,
        actualPrice: Math.round(actualPrice * 100) / 100,
        discountPerUnit: Math.round(discountPerUnit * 100) / 100,
        totalDiscount,
        type: 'discount',
      })
    }
  }

  items.sort((a, b) => b.totalDiscount - a.totalDiscount)
  freeItems.sort((a, b) => b.totalLoss - a.totalLoss)

  const totalDiscount = Math.round(items.reduce((s, i) => s + i.totalDiscount, 0) * 100) / 100
  const totalFreeLoss = Math.round(freeItems.reduce((s, i) => s + i.totalLoss, 0) * 100) / 100

  return {
    items,
    freeItems,
    totalDiscount,
    totalFreeLoss,
    affectedProducts: items.length,
    affectedFreeProducts: freeItems.length,
  }
}
